/**
 * Cross-platform "that field is wrong" feedback for form validation.
 *
 * Why this exists: the forms used to signal a validation error with
 * `navigator.vibrate([80, 40, 80])`, which works on Android Chrome but does
 * nothing on any iPhone — iOS Safari has never implemented the Vibration API,
 * and since every iOS browser is WebKit there is no browser to switch to.
 * (The old `<input type="checkbox" switch>` haptic trick covered iOS 17.4–26.4
 * but Apple closed it in iOS 26.5, so there is no reliable web haptic on iOS.)
 *
 * So: keep the buzz where it works, and add a short shake so iPhone users get
 * the same physical "something's wrong" cue. The shake runs on every platform —
 * it is the primary signal, the vibration is a bonus on top.
 *
 * The visible error text, the warning toast and the scroll-to-field all stay
 * as they were; this only replaces the part that silently did nothing on iOS.
 */

const SHAKE_CLASS = "tx-shake";

/** Android-only; a no-op on iOS, which is exactly why shakeField exists. */
export function buzzError() {
  try {
    if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
      navigator.vibrate([80, 40, 80]);
    }
  } catch {
    /* some browsers throw if vibration is blocked by a permissions policy */
  }
}

/**
 * Shake an element once. Safe to call repeatedly — the class is removed first
 * so a second failed submit re-triggers the animation instead of doing nothing
 * (re-adding a class that is already present does not restart a CSS animation).
 */
export function shakeField(el) {
  if (!el || !el.classList) return;
  el.classList.remove(SHAKE_CLASS);
  // Force a reflow so the browser treats the re-add as a fresh animation.
  void el.offsetWidth;
  el.classList.add(SHAKE_CLASS);
  const done = () => {
    el.classList.remove(SHAKE_CLASS);
    el.removeEventListener("animationend", done);
  };
  el.addEventListener("animationend", done);
  // Fallback: if the animation never fires (reduced-motion, element detached),
  // drop the class anyway so it can't stick around on the field.
  setTimeout(done, 600);
}

/**
 * The whole error gesture for a failed submit: buzz on Android, then scroll the
 * offending field into view and shake + focus it once the scroll has settled.
 *
 * The delay matters — a smooth scroll takes roughly a third of a second, and
 * shaking immediately would animate the field while it is still off-screen.
 */
export function flagInvalidField(el, { delay = 350 } = {}) {
  buzzError();
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  setTimeout(() => {
    shakeField(el);
    el.focus?.();
  }, delay);
}
